import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toast } from "@/lib/safe-toast";
import { Copy, DollarSign, Users, TrendingUp } from "lucide-react";
import { authenticatedRoute } from "../_authenticated";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  StatCard,
} from "@/components/ui";
import { toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";
import {
  useAffiliatePayouts,
  useAffiliateStatus,
  useEnrollAffiliate,
} from "@/hooks/useAffiliate";

const AFFILIATE_MAIL = "affiliate@takelead.app";

function AffiliatePage() {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.language || "en";
  const { data: status, isLoading: statusLoading, error: statusError } = useAffiliateStatus();
  const { data: payouts, isLoading: payoutsLoading, error: payoutsError } = useAffiliatePayouts();
  const enroll = useEnrollAffiliate();
  const [copied, setCopied] = useState(false);

  const affiliateLink =
    status?.affiliateCode != null
      ? `https://takelead.app/a/${toDisplayString(status.affiliateCode)}`
      : null;

  const rawCommission = Number(status?.commissionPercent);
  const commissionPercent = Number.isFinite(rawCommission) ? rawCommission : 25;

  const onEnroll = async () => {
    try {
      await enroll.mutateAsync();
      toast.success(t("affiliate.enrollSuccess"));
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("affiliate.enrollError"));
    }
  };

  const copyLink = () => {
    if (!affiliateLink) return;
    navigator.clipboard.writeText(affiliateLink).then(
      () => {
        setCopied(true);
        toast.success(t("affiliate.linkCopied"));
        setTimeout(() => setCopied(false), 2000);
      },
      () => toast.error(t("affiliate.copyError"))
    );
  };

  const currency = payouts?.currency ?? "EUR";
  const fmtCents = (cents: unknown) =>
    `${(Number(cents) / 100).toFixed(2)} ${toDisplayString(currency)}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("affiliate.title"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("affiliate.subtitle"))}</p>
      </div>

      {statusLoading && <p className="text-sm text-text-muted">{toSafeReactNode(t("affiliate.statusLoading"))}</p>}
      {statusError && <p className="text-sm text-danger">{toErrorDisplayMessage(statusError)}</p>}

      {status && !status.enrolled && (
        <Card>
          <CardHeader>
            <CardTitle>{toSafeReactNode(t("affiliate.enrollTitle"))}</CardTitle>
            <CardDescription>{toSafeReactNode(t("affiliate.enrollDescription", { percent: commissionPercent }))}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="primary"
              onClick={onEnroll}
              disabled={enroll.isPending}
            >
              {enroll.isPending ? toSafeReactNode(t("affiliate.enrolling")) : toSafeReactNode(t("affiliate.enrollButton"))}
            </Button>
            <p className="text-xs text-text-muted">
              {toSafeReactNode(t("affiliate.questions"))}{" "}
              <a
                href={`mailto:${AFFILIATE_MAIL}?subject=${encodeURIComponent(toDisplayString(t("affiliate.mailSubjectEnroll")))}`}
                className="text-accent hover:underline"
              >
                {AFFILIATE_MAIL}
              </a>
            </p>
          </CardContent>
        </Card>
      )}

      {status?.enrolled && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("affiliate.yourLink"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("affiliate.yourLinkDescription", { percent: commissionPercent }))}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <code className="flex-1 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text break-all">
                  {affiliateLink}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={copyLink}
                  disabled={!affiliateLink}
                  className="shrink-0"
                >
                  <Copy className="mr-1 h-4 w-4" aria-hidden />
                  {copied ? toSafeReactNode(t("referrals.copied")) : toSafeReactNode(t("referrals.copy"))}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              label={toDisplayString(t("affiliate.totalCommission"))}
              value={fmtCents(status.totalCommissionCents)}
              icon={<DollarSign className="h-4 w-4" />}
            />
            <StatCard
              label={toDisplayString(t("affiliate.conversions"))}
              value={status.conversions.length}
              icon={<Users className="h-4 w-4" />}
            />
            <StatCard
              label={toDisplayString(t("affiliate.commissionRate"))}
              value={`${commissionPercent} %`}
              trend={toDisplayString(t("affiliate.trendOneTime"))}
              icon={<TrendingUp className="h-4 w-4" />}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("affiliate.payouts"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("affiliate.payoutsDescription"))}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {payoutsLoading && (
                <p className="text-sm text-text-muted">{toSafeReactNode(t("affiliate.payoutsLoading"))}</p>
              )}
              {payoutsError && (
                <p className="text-sm text-danger">{toErrorDisplayMessage(payoutsError)}</p>
              )}
              {payouts && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between rounded-md border border-border bg-surface-2 px-3 py-2">
                    <span className="text-text-muted">{toSafeReactNode(t("affiliate.open"))}</span>
                    <span className="font-medium text-text">{fmtCents(payouts.unpaidCents)}</span>
                  </div>
                  <div className="flex justify-between rounded-md border border-border bg-surface-2 px-3 py-2">
                    <span className="text-text-muted">{toSafeReactNode(t("affiliate.paid"))}</span>
                    <span className="font-medium text-text">{fmtCents(payouts.paidCents)}</span>
                  </div>
                  <p className="text-xs text-text-muted">
                    {toSafeReactNode(t("affiliate.minAmount", { amount: fmtCents(payouts.minPayoutMinorUnits) }))}
                  </p>
                  {payouts.canRequestPayout && (
                    <div className="rounded-md border border-success/30 bg-success/10 p-3 text-sm text-success">
                      {toSafeReactNode(t("affiliate.payoutPossibleBefore"))}
                      <a
                        href={`mailto:${AFFILIATE_MAIL}?subject=${encodeURIComponent(toDisplayString(t("affiliate.mailSubjectPayout")))}`}
                        className="underline"
                      >
                        {AFFILIATE_MAIL}
                      </a>
                      {toSafeReactNode(t("affiliate.payoutPossibleAfter"))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{toSafeReactNode(t("affiliate.conversionsTitle"))}</CardTitle>
              <CardDescription>{toSafeReactNode(t("affiliate.conversionsDescription"))}</CardDescription>
            </CardHeader>
            <CardContent>
              {status.conversions.length === 0 ? (
                <p className="text-sm text-text-muted">{toSafeReactNode(t("affiliate.noConversionsYet"))}</p>
              ) : (
                <ul className="space-y-2">
                  {status.conversions.map((c) => (
                    <li
                      key={toDisplayString(c.id)}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="text-text">
                          {toSafeReactNode(t("affiliate.revenueCommission", {
                            revenue: fmtCents(c.revenueCents),
                            commission: fmtCents(c.commissionCents),
                          }))}
                        </p>
                        <p className="text-xs text-text-muted">
                          {new Date(c.createdAt).toLocaleDateString(toDisplayString(locale))}
                        </p>
                      </div>
                      <span
                        className={
                          c.paid
                            ? "rounded-md bg-success/20 px-2 py-0.5 text-xs text-success"
                            : "rounded-md bg-amber-500/20 px-2 py-0.5 text-xs text-amber-400"
                        }
                      >
                        {c.paid ? toSafeReactNode(t("affiliate.paidOut")) : toSafeReactNode(t("affiliate.openStatus"))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export const affiliateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "affiliate",
  component: AffiliatePage,
});
